// src/pages/settings/helpers.ts — fonctions et constantes partagées entre les onglets de Paramètres

import type { SiteRecord } from "../../api/settingsApi";
import type { LatLon } from "../../api/geoApi";

export const MAX_W_150DPI = Math.round(210 * 150 / 25.4); // 1240 px — A4 @ 150 dpi

/** Coordonnées enregistrées dans la fiche du site (SiteForm géocode l'adresse à l'enregistrement). */
export function siteLatLon(s: SiteRecord): LatLon | null {
  const lat = parseFloat(s.data.coverProps?.lat ?? "");
  const lon = parseFloat(s.data.coverProps?.lon ?? "");
  return (Number.isFinite(lat) && Number.isFinite(lon)) ? { lat, lon } : null;
}

/** True si une station Météo-France est rattachée au site (choisie via WeatherPointModal) — sinon les blocs DJU du rapport (rigueur climatique, prévision) échouent en 422. */
export function siteHasWeatherStation(s: SiteRecord): boolean {
  return Number.isFinite(parseInt(s.data.coverProps?.meteoFranceStationId ?? "", 10));
}

export const b64Bytes = (b64: string) =>
  Math.floor(b64.length * 3/4) - (b64.endsWith("==") ? 2 : b64.endsWith("=") ? 1 : 0);
export const fmtBytes = (b: number) =>
  b >= 1024*1024 ? `${(b/1024/1024).toFixed(2)} Mo` : `${(b/1024).toFixed(1)} Ko`;

export function toMaxH(dataUrl: string, maxH: number): Promise<string> {
  return new Promise(res => {
    if (dataUrl.startsWith("data:image/svg")) { res(dataUrl); return; }
    const img = new Image();
    img.onload = () => {
      if (img.naturalHeight <= maxH) { res(dataUrl); return; }
      const scale  = maxH / img.naturalHeight;
      const canvas = document.createElement("canvas");
      canvas.width  = Math.round(img.naturalWidth * scale);
      canvas.height = maxH;
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      res(canvas.toDataURL("image/png"));
    };
    img.src = dataUrl;
  });
}

export function to150dpi(dataUrl: string): Promise<string> {
  return new Promise(res => {
    if (dataUrl.startsWith("data:image/svg")) { res(dataUrl); return; }
    const isPng = dataUrl.startsWith("data:image/png");
    const img = new Image();
    img.onload = () => {
      const needsResize = img.naturalWidth > MAX_W_150DPI;
      // JPEG already within size limit → return as-is; PNG always re-encodes to JPEG
      if (!needsResize && !isPng) { res(dataUrl); return; }
      const scale  = needsResize ? MAX_W_150DPI / img.naturalWidth : 1;
      const canvas = document.createElement("canvas");
      canvas.width  = needsResize ? MAX_W_150DPI : img.naturalWidth;
      canvas.height = Math.round(img.naturalHeight * scale);
      canvas.getContext("2d")!.drawImage(img, 0, 0, canvas.width, canvas.height);
      res(canvas.toDataURL("image/jpeg", 0.88));
    };
    img.src = dataUrl;
  });
}

export function toCamelCase(s: string): string {
  return s.normalize("NFD").replace(/\p{Diacritic}/gu,"")
    .trim().replace(/[^a-zA-Z0-9 ]+/g,"")
    .split(" ").filter(Boolean)
    .map((w,i) => i===0 ? w.toLowerCase() : w[0].toUpperCase()+w.slice(1).toLowerCase())
    .join("");
}

export function makeSiteId(clientId: string, cp: string, existingIds: string[]): string {
  const base = `${clientId}@${cp.trim().replace(/\s+/g,"-")}`;
  if (!existingIds.includes(base)) return base;
  let n = 2;
  while (existingIds.includes(`${base}-${n}`)) n++;
  return `${base}-${n}`;
}

// ── Meter entry (PDL / PCE) ───────────────────────────────────────────────────
export type MeterEntry = { id: string; label: string };

// ── Cover prop labels (non-list fields) ───────────────────────────────────────
export const PROP_LABELS: [string,string][] = [
  ["companyName",        "Nom de l'entreprise"],
  ["anneeConstruction",  "Année de construction"],
  ["anneeRehabiliation", "Année de réhabilitation"],
  ["occupant",           "Occupant"],
  ["configuration",      "Configuration"],
  ["typologie",          "Typologie"],
  ["surface",            "Surface (m²)"],
  ["horaire",            "Horaires"],
  ["chauffage",          "Chauffage"],
  ["rafraichissement",   "Rafraîchissement"],
];
